import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  ShoppingBag,
  Package,
  Flower2,
  Info,
  Tag,
  Truck,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';

export interface CiceksepetiCredentials {
  apiKey: string;
}

interface CiceksepetiConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (credentials: CiceksepetiCredentials, connectionId: string) => void;
}

export function CiceksepetiConnectModal({
  isOpen,
  onClose,
  onSuccess,
}: CiceksepetiConnectModalProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const { user } = useAuth();
  const { checkDuplicateApiKey } = useSubscription();

  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'validating' | 'success'>('form');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!apiKey.trim()) {
      setError(isEnglish ? 'API key is required' : 'API anahtari zorunludur');
      return;
    }

    if (!user?.uid) {
      setError(isEnglish ? 'Please login first' : 'Lutfen once giris yapin');
      return;
    }

    setIsValidating(true);
    setStep('validating');

    try {
      // Check for duplicate API key
      const duplicateCheck = await checkDuplicateApiKey(apiKey.trim(), 'ciceksepeti');
      if (duplicateCheck.isDuplicate) {
        setStep('form');
        setError(duplicateCheck.message || (isEnglish
          ? 'Duplicate account detected! This API key is already used in another account.'
          : 'Mukerrer hesap tespit edildi! Bu API anahtari baska bir hesapta kullanilmaktadir.'));
        setIsValidating(false);
        return;
      }

      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';

      // Save connection via Cloud Function (validates and saves to Firestore)
      const response = await fetch(`${FUNCTIONS_URL}/saveCiceksepeti`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          apiKey: apiKey.trim(),
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setStep('form');
        if (response.status === 429) {
          setError(isEnglish ? 'API rate limit exceeded. Please wait a moment.' : 'API limit asildi. Lutfen biraz bekleyin.');
        } else {
          setError(result.error || (isEnglish ? 'Invalid API key' : 'Gecersiz API anahtari'));
        }
        setIsValidating(false);
        return;
      }

      // Success!
      const credentials: CiceksepetiCredentials = {
        apiKey: apiKey.trim(),
      };

      setStep('success');
      setTimeout(() => {
        onSuccess(credentials, result.connectionId);
        handleClose();
      }, 1500);
    } catch (err) {
      setStep('form');
      setError(
        err instanceof Error
          ? err.message
          : isEnglish
          ? 'Connection failed. Please try again.'
          : 'Baglanti basarisiz. Lutfen tekrar deneyin.'
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleClose = () => {
    setApiKey('');
    setShowApiKey(false);
    setError(null);
    setStep('form');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-surface rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-border-default">
        {/* Header with Ciceksepeti Branding */}
        <div
          className="relative p-6 pb-8"
          style={{
            background: 'linear-gradient(135deg, #7B2D8E 0%, #9B4DB5 100%)',
          }}
        >
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <X size={18} />
          </button>

          <div className="flex items-center gap-4">
            {/* Ciceksepeti Logo */}
            <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center shadow-lg">
              <Flower2 size={32} className="text-[#7B2D8E]" />
            </div>
            <div className="text-white">
              <h2 className="text-xl font-bold">Ciceksepeti</h2>
              <p className="text-white/80 text-sm">
                {isEnglish ? 'Connect your seller account' : 'Satici hesabinizi baglayin'}
              </p>
            </div>
          </div>

          {/* Features */}
          <div className="flex flex-wrap gap-3 mt-6">
            {[
              { icon: ShoppingBag, label: isEnglish ? 'Orders' : 'Siparisler' },
              { icon: Package, label: isEnglish ? 'Products' : 'Urunler' },
              { icon: Tag, label: isEnglish ? 'Categories' : 'Kategoriler' },
              { icon: Truck, label: isEnglish ? 'Shipping' : 'Kargo' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-white/90 text-sm">
                <Icon size={14} />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'validating' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-[#7B2D8E]/10 flex items-center justify-center mx-auto mb-4">
                <Loader2 size={32} className="text-[#7B2D8E] animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {isEnglish ? 'Validating API key...' : 'API anahtari dogrulaniyor...'}
              </h3>
              <p className="text-sm text-text-secondary">
                {isEnglish
                  ? 'Connecting to Ciceksepeti API'
                  : 'Ciceksepeti API\'sine baglaniliyor'}
              </p>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-green-500" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {isEnglish ? 'Successfully connected!' : 'Basariyla baglandi!'}
              </h3>
              <p className="text-sm text-text-secondary">
                {isEnglish
                  ? 'Your Ciceksepeti account is now connected'
                  : 'Ciceksepeti hesabiniz artik bagli'}
              </p>
            </div>
          )}

          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error Banner */}
              {error && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                  <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* Info Banner */}
              <div className="flex items-start gap-3 p-4 rounded-xl bg-[#7B2D8E]/10 border border-[#7B2D8E]/30">
                <Info size={20} className="text-[#7B2D8E] flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-[#7B2D8E] font-medium mb-1">
                    {isEnglish ? 'Where to find your API key?' : 'API anahtarinizi nereden bulabilirsiniz?'}
                  </p>
                  <p className="text-text-secondary">
                    {isEnglish
                      ? 'Go to Ciceksepeti Seller Panel → Settings → API Settings'
                      : 'Ciceksepeti Satici Paneli → Ayarlar → API Ayarlari'}
                  </p>
                </div>
              </div>

              {/* API Key */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  API {isEnglish ? 'Key' : 'Anahtari'}
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="w-full px-4 py-3 pr-12 rounded-xl bg-bg-elevated border border-border-default focus:border-[#7B2D8E] focus:outline-none focus:ring-2 focus:ring-[#7B2D8E]/20 text-text-primary placeholder:text-text-tertiary transition-all font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary transition-colors"
                  >
                    {showApiKey ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Rate Limit Warning */}
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-600">
                  {isEnglish
                    ? 'Note: Ciceksepeti API has rate limits. Product listing can be queried once every 10 minutes.'
                    : 'Not: Ciceksepeti API rate limit uygulamaktadir. Urun listeleme 10 dakikada 1 kez sorgulanabilir.'}
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 rounded-xl bg-bg-elevated border border-border-default text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-all font-medium"
                >
                  {isEnglish ? 'Cancel' : 'Iptal'}
                </button>
                <button
                  type="submit"
                  disabled={isValidating}
                  className={cn(
                    'flex-1 px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2',
                    'bg-[#7B2D8E] text-white hover:bg-[#6B2580]',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {isValidating ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {isEnglish ? 'Connecting...' : 'Baglaniyor...'}
                    </>
                  ) : (
                    isEnglish ? 'Connect' : 'Baglan'
                  )}
                </button>
              </div>

              {/* Help Link */}
              <div className="text-center pt-2">
                <a
                  href="https://ciceksepeti.dev/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-text-tertiary hover:text-[#7B2D8E] transition-colors"
                >
                  <ExternalLink size={14} />
                  {isEnglish ? 'API Documentation' : 'API Dokumantasyonu'}
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
